import useRotation from '../../app/useRotation';
import { ReportLocationEnum } from '../../enums/reportLocationEnum';
import { RotationTreeEnum } from '../../enums/rotationTreeEnum';
import { MoveActionMsg } from '../../types/ViewModel/ActionMsg';
import { BerthCall } from '../../types/ViewModel/BerthCall';
import { CargoOperation } from '../../types/ViewModel/CargoOperation';
import { EventTypeData } from '../../types/ViewModel/EventTypeData';
import { IRotationNode } from '../../types/ViewModel/IRotationNode';
import { PortCall } from '../../types/ViewModel/PortCall';
import { Rotation } from '../../types/ViewModel/Rotation';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import eventtypehandler from './eventtypehandler';

export default () => {
    const {
        getParentPortForMovedToBerthCall,
        getParentPortCallFromNode,
        getParentBerthCallFromNode,
        getParentBerthForMovedToCargoOp,
        getBerthCallsForPortCall,
        getParentBerthForMovedFromCargoOp,
        getParentBerthForMovedToCargoOpSamePortCall,
        moveToPortCallItem,
        getPortCall,
    } = useRotation();
    const { canMoveEventsForMovedCargoOp } = eventtypehandler();

    const getDeletedIconTooltipText = (rotation: Rotation, node: IRotationNode): string | null => {
        switch (node.type) {
            case RotationTreeEnum.PortCall:
                return 'This port call is deleted from the current stowage master plan';
            case RotationTreeEnum.BerthCall: {
                const bc: BerthCall = node as BerthCall;
                if (bc.isDeleted) {
                    return 'This berth call is deleted from the current stowage master plan';
                } else if (bc.isMoved) {
                    const pcToParent = getParentPortForMovedToBerthCall(rotation, bc.berthCallId);
                    if (pcToParent) {
                        const pcFromParent = getParentPortCallFromNode(bc);
                        if (pcFromParent) {
                            if (pcToParent.portCallId !== pcFromParent.portCallId) {
                                return (
                                    "This berth call has been moved to port call '" +
                                    pcToParent.voyageNumberPresentationString +
                                    ' ' +
                                    pcToParent.name +
                                    "'"
                                );
                            }
                        }
                    }
                    return 'This berth call has been moved to an unknown port call in the stowage master plan';
                }
                break;
            }
            case RotationTreeEnum.CargoOperation: {
                const co: CargoOperation = node as CargoOperation;
                if (co.isDeleted) {
                    return 'This cargo operation is deleted from the current stowage master plan';
                } else if (co.isMoved) {
                    const bcFromParent = getParentBerthCallFromNode(co);
                    if (bcFromParent) {
                        const pcFromParent = getParentPortCallFromNode(bcFromParent);
                        if (pcFromParent) {
                            const bcToParent = getParentBerthForMovedToCargoOp(rotation, co.cargoOperationId);
                            if (bcToParent) {
                                const pcToParent = getParentPortCallFromNode(bcToParent);
                                if (pcToParent) {
                                    if (pcToParent.portCallId !== pcFromParent.portCallId) {
                                        return (
                                            "This cargo operation has been moved to berth call '" +
                                            bcToParent.voyageNumber +
                                            ' ' +
                                            bcToParent.name +
                                            "' in port call '" +
                                            pcToParent.voyageNumberPresentationString +
                                            ' ' +
                                            pcToParent.name +
                                            "'"
                                        );
                                    } else {
                                        // TODO: LOGIC-ERROR - Typo in berth call reference - using bcFromParent.voyageNumber instead of bcToParent.voyageNumber in the 'moved to' message creates misleading text
                                        return (
                                            "This cargo operation has been moved to berth call '" +
                                            bcFromParent.voyageNumber +
                                            ' ' +
                                            bcFromParent.name +
                                            "'"
                                        );
                                    }
                                }
                            }
                        }
                    }
                    return 'This cargo operation has been moved to an unknown berth in the stowage master plan';
                }
                break;
            }
        }
        return null;
    };

    const getCargoOperationToolTip = (
        rot: Rotation,
        eventTypeData: EventTypeData,
        eventList: TankersEvent[],
        cargoOp: CargoOperation
    ): MoveActionMsg => {
        const actionMsg = new MoveActionMsg(false, null, false, null, null, null);
        if (cargoOp.isDeleted) {
            actionMsg.message = 'This cargo operation has been deleted from the stowage master plan and is no longer in this berth call';
        } else if (cargoOp.isMoved) {
            return moveToCargoOpText(rot, eventTypeData, eventList, cargoOp);
        }
        return actionMsg;
    };

    const getBerthCallToolTip = (rotation: Rotation, eventList: TankersEvent[], berthCall: BerthCall): MoveActionMsg => {
        return moveToBerthCallText(rotation, eventList, berthCall);
    };

    // Set locked flag on berth call if:
    //  - The berth call has at least one cargo operation
    //  - The berth call has no registered events (if not already unlocked by user)
    //  - The port call has more berth calls than this berth call
    //  - The source berth call has at least one registered event
    //  - It is not possible to move the events for the source berth call and all included cargo operations
    //  - At least one cargo operation must have been moved from the source berth call to this berth call
    const setLockedBerthCall = (rotation: Rotation, eventList: TankersEvent[], unlockBerthCall: BerthCall): boolean => {
        let isLocked = false;

        // At least one cargo operation
        if (!unlockBerthCall.cargoOperationArray || unlockBerthCall.cargoOperationArray.length === 0) {
            return false;
        }

        // TODO: NULL-SAFETY - getBerthCallsForPortCall might return null or undefined, but it's used directly in filter operation
        const bcList = getBerthCallsForPortCall(rotation, unlockBerthCall.portCall?.portCallId);
        const bcListExcludeThis = bcList.filter((bc) => bc.berthCallId !== unlockBerthCall.berthCallId);
        if (!bcListExcludeThis || bcListExcludeThis.length === 0) {
            return false;
        }

        bcListExcludeThis.forEach((bc) => {
            // Find events for evListFrom
            const evListFrom = eventList.filter((ev) => !ev.deleted && ev.berthCallId === bc.berthCallId);
            if (evListFrom && evListFrom.length > 0) {
                if (!moveToBerthCallText(rotation, eventList, bc)) {
                    if (bc.cargoOperationArray && bc.cargoOperationArray.length > 0) {
                        let targetCoHasEvents = false;
                        unlockBerthCall.cargoOperationArray.forEach((coUnlock) => {
                            const coFromFound = bc.cargoOperationArray.find(
                                (coFrom) => coFrom.isMoved && coFrom.cargoOperationId === coUnlock.cargoOperationId
                            );
                            if (coFromFound) {
                                const coUnlockEvList = eventList.filter(
                                    (ev) =>
                                        !ev.deleted &&
                                        ev.berthCallId === unlockBerthCall.berthCallId &&
                                        ev.cargoOperationId &&
                                        ev.cargoOperationId === coFromFound.cargoOperationId
                                );
                                if (coUnlockEvList && coUnlockEvList.length > 0) {
                                    targetCoHasEvents = true;
                                }
                                isLocked = true;
                            }
                        });
                        isLocked = isLocked && !targetCoHasEvents;
                    }
                }
            }
        });
        return isLocked;
    };

    const unlockedByUser = () => {
        // const alreadyUnlockedIndex = _.findIndex(this.unlockedBerthCalls, bc => bc === berthCallId);
        // if (alreadyUnlockedIndex > -1) {
        //   if (!unlock) {
        //     this.unlockedBerthCalls.splice(alreadyUnlockedIndex, 1);
        //   }
        // } else {
        //   if (unlock) {
        //     this.unlockedBerthCalls.push(berthCallId);
        //   }
        // }
    };

    const setUnlockedByUserBerthCall = (eventList: TankersEvent[], unlockBerthCall: BerthCall): boolean => {
        const evListTo = eventList.filter((ev) => !ev.deleted && ev.berthCallId === unlockBerthCall.berthCallId);
        return evListTo && evListTo.length > 0;
    };

    // Can move events from this cargo operation if (i.e. the menu item for move events appears):
    //  - The cargo operation is moved
    //  - The cargo operation has at least one registered event
    //  - The 'move-to' cargo operation cannot have any registered events
    const moveToCargoOpText = (
        rot: Rotation,
        eventTypeData: EventTypeData,
        eventList: TankersEvent[],
        moveFromCargoOp: CargoOperation
    ): MoveActionMsg => {
        const actionMsg = new MoveActionMsg(false, null, false, null, null, null);

        if (!moveFromCargoOp.isMoved) {
            return actionMsg;
        }
        const coMovedFromBc = getParentBerthForMovedFromCargoOp(moveFromCargoOp);
        if (coMovedFromBc) {
            // Find moved cargo operation
            const coMovedToBc = getParentBerthForMovedToCargoOpSamePortCall(moveFromCargoOp);
            if (coMovedToBc) {
                actionMsg.id = coMovedToBc.id;
                actionMsg.displayMoveIcon = true;
                const evList = eventList.filter(
                    (ev) => !ev.deleted && ev.cargoOperationId === moveFromCargoOp.cargoOperationId && ev.berthCallId === coMovedFromBc.berthCallId
                );
                if (!evList || evList.length === 0) {
                    return actionMsg;
                }
                const doAct = canMoveEventsForMovedCargoOp(rot, eventList, eventTypeData, coMovedToBc, moveFromCargoOp, moveFromCargoOp);
                if (doAct.doAction) {
                    actionMsg.message = "Move events to berth call '" + coMovedToBc.name + "'?";
                    actionMsg.doAction = true;
                    // coMovedToBc.isExpanded = true;
                    // coMovedFromBc.isExpanded = true;
                } else {
                    actionMsg.message = doAct.message;
                    actionMsg.doAction = false;
                }
                actionMsg.toolTipText = "Move events to berth call '" + coMovedToBc.name + "'?";
            }
        }
        return actionMsg;
    };

    // Can move events from this berth call if (i.e. the menu item for move events appears):
    //  - The berth call is deleted
    //  - The berth call has at least one cargo operation
    //  - The berth call has at least one registered event
    //  - The port call has more berth calls than this berth call
    //  - One of the other berth calls must have the same cargo operation as this berth call (the 'move-to' berth call)
    //  - The 'move-to' berth call cannot have any registered events
    //  - The 'move-to' berth call need to be in the same port call
    //  - The 'move-to' berth does not need to be the same as the deleted berth
    const moveToBerthCallText = (rotation: Rotation, eventList: TankersEvent[], moveFromBerthCall: BerthCall): MoveActionMsg => {
        const actionMsg = new MoveActionMsg(false, null, false, null, null, null);

        if (!moveFromBerthCall.isDeleted) {
            return actionMsg;
        }

        if (!moveFromBerthCall.cargoOperationArray || moveFromBerthCall.cargoOperationArray.length === 0) {
            return actionMsg;
        }

        // Find events for moveFromBerthCall
        const evList = eventList.filter((ev) => !ev.deleted && ev.berthCallId === moveFromBerthCall.berthCallId);
        if (!evList || evList.length === 0) {
            return actionMsg;
        }

        // Find berth calls for this port call
        const bcList = getBerthCallsForPortCall(rotation, moveFromBerthCall.portCall?.portCallId);
        const bcListExcludeThis = bcList.filter((bc) => bc.berthCallId !== moveFromBerthCall.berthCallId);
        if (!bcListExcludeThis || bcListExcludeThis.length === 0) {
            return actionMsg;
        }

        bcListExcludeThis.forEach((bc) => {
            // Find events for moveFromBerthCall
            const evListToBerth = eventList.filter((ev) => !ev.deleted && ev.berthCallId === bc.berthCallId);
            if (!evListToBerth || evListToBerth.length === 0) {
                const sameCargoOp = bc.cargoOperationArray.find(
                    (co) =>
                        co.cargoOperationId === moveFromBerthCall.cargoOperationArray[0].cargoOperationId &&
                        moveFromBerthCall.cargoOperationArray[0].isMoved
                );
                if (sameCargoOp) {
                    let allCargoExist = true;
                    moveFromBerthCall.cargoOperationArray.forEach((coFrom) => {
                        const coToFound = bc.cargoOperationArray.find((coTo) => coFrom.isMoved && coTo.cargoOperationId === coFrom.cargoOperationId);
                        if (!coToFound) {
                            allCargoExist = false;
                        }
                    });
                    if (allCargoExist) {
                        actionMsg.displayMoveIcon = true;
                        actionMsg.message = "Move events to berth call '" + bc.name + "'?";
                        actionMsg.doAction = true;
                        actionMsg.toolTipText = "Move events to berth call '" + bc.name + "'?";
                        actionMsg.id = bc.id;
                    }
                }
            }
        });
        return actionMsg;
    };

    // Can move events from a port call if (i.e. the menu item for move events appears):
    //  - The port call is deleted
    //  - The port call has at least one berth call
    //  - The port call has at least one registered event
    //  - The 'move-to' port call must have the same port id and berth id’s as the deleted port call
    //  - The 'move-to' port call/berth call/cargo operations cannot have any registered events
    //  - The 'move-to' cargo operations must have the same operation type (load/unload), weight and imos cargo id (if present, otherwise the cargo id must be the same)
    //  - Port Cluster support:
    //      -
    const getMoveToPortCallText = (rotation: Rotation, eventList: TankersEvent[], moveFromPortCall: PortCall): MoveActionMsg => {
        const actionMsg = new MoveActionMsg(false, null, false, null, null, null);
        const moveEventsPortCall = moveToPortCallItem(rotation.portCallArray, eventList, moveFromPortCall);
        if (moveEventsPortCall) {
            const pc = getPortCall(rotation, moveEventsPortCall.toPortCallId, ReportLocationEnum.Port);
            if (pc) {
                actionMsg.displayMoveIcon = true;
                actionMsg.message = "Move events to port call '" + pc.voyageNumberPresentationString + ' ' + pc.name + "'?";
                actionMsg.doAction = true;
                actionMsg.toolTipText = "Move events to port call '" + pc.voyageNumberPresentationString + ' ' + pc.name + "'?";
                actionMsg.id = pc.id;
            }
        }
        return actionMsg;
    };

    const createActionMsg = (actMsg: MoveActionMsg, actMsgUpd) => {
        actMsgUpd.doAction = actMsg.doAction;
        actMsgUpd.displayMoveIcon = actMsg.displayMoveIcon;
        actMsgUpd.moveToNodeText = actMsg.message;
        actMsgUpd.toolTipText = actMsg.toolTipText;
        actMsgUpd.message = actMsg.message;
        actMsgUpd.id = actMsg.id;
    };

    const updateMovedToFlag = (rotation: Rotation) => {
        rotation.portCallArray.forEach((pcItem) => {
            if (pcItem.actionMsg.displayMoveIcon && pcItem.actionMsg.id) {
                const pc = rotation.portCallArray.find((o) => o.portCallId === pcItem.actionMsg.id);
                if (pc) {
                    pc.isMovedTo = true;
                    pc.actionMsg.toolTipText = 'Move events from port call ' + pcItem.voyageNumberPresentationString + ' ' + pcItem.name;
                }
            }
            const bcList = rotation.portCallArray.flatMap((o) => o.berthCallArray);
            bcList.forEach((bcItem) => {
                if (bcItem.actionMsg.displayMoveIcon && bcItem.actionMsg.id) {
                    const bc = bcList.find((o) => o.berthCallId === bcItem.actionMsg.id);
                    if (bc) {
                        bc.isMovedTo = true;
                        bc.actionMsg.toolTipText = 'Move events from berth call ' + bcItem.name;
                    }
                }
            });
            const coList = bcList.flatMap((o) => o.cargoOperationArray);
            coList.forEach((coItem) => {
                if (coItem.actionMsg.displayMoveIcon && coItem.actionMsg.id) {
                    const bc = bcList.find((o) => o.berthCallId === coItem.actionMsg.id);
                    if (bc) {
                        const co = coList.find((o) => o.berthCall?.berthCallId === bc.berthCallId && o.cargoOperationId === coItem.cargoOperationId);
                        if (co) {
                            const berthCall = getParentBerthCallFromNode(co);
                            if (berthCall) {
                                co.isMovedTo = true;
                                co.actionMsg.toolTipText = 'Move events from cargo operation ' + coItem.name + ' in berth call ' + berthCall.name;
                            }
                        }
                    }
                }
            });
        });
    };

    const updateRotationNodeFlags = (eventTypeData: EventTypeData, eventArray: TankersEvent[], rotation: Rotation) => {
        rotation.portCallArray.forEach((pcItem) => {
            createActionMsg(getMoveToPortCallText(rotation, eventArray, pcItem), pcItem.actionMsg);
            if (!pcItem.actionMsg.displayMoveIcon) {
                pcItem.berthCallArray.forEach((bcItem) => {
                    createActionMsg(moveToBerthCallText(rotation, eventArray, bcItem), bcItem.actionMsg);
                    bcItem.cargoOperationArray.forEach((coItem) => {
                        createActionMsg(moveToCargoOpText(rotation, eventTypeData, eventArray, coItem), coItem.actionMsg);
                    });
                });
            }
        });
    };

    const updateRotationNodeTooltipFlags = (eventTypeData: EventTypeData, eventArray: TankersEvent[], rotation: Rotation) => {
        rotation.portCallArray.forEach((pcItem) => {
            pcItem.deletedIconTooltipText = getDeletedIconTooltipText(rotation, pcItem);
            pcItem.berthCallArray.forEach((bcItem) => {
                const actionMsgBc = getBerthCallToolTip(rotation, eventArray, bcItem);
                bcItem.actionMsg.toolTipText = actionMsgBc.message;
                bcItem.deletedIconTooltipText = getDeletedIconTooltipText(rotation, bcItem);
                bcItem.cargoOperationArray.forEach((coItem) => {
                    const actionMsgCo = getCargoOperationToolTip(rotation, eventTypeData, eventArray, coItem);
                    coItem.actionMsg.toolTipText = actionMsgCo.message;
                    coItem.deletedIconTooltipText = getDeletedIconTooltipText(rotation, coItem);
                });
            });
        });
    };

    return {
        getMoveToPortCallText,
        setLockedBerthCall,
        setUnlockedByUserBerthCall,
        unlockedByUser,
        moveToBerthCallText,
        moveToCargoOpText,
        getDeletedIconTooltipText,
        getBerthCallToolTip,
        getCargoOperationToolTip,
        updateRotationNodeFlags,
        updateRotationNodeTooltipFlags,
        updateMovedToFlag,
    };
};
